import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Modal from '../modal';
import Button from '../button';
import Tabs from '../tabs';
import SelectMenu from '../select';
import { PREFERENCES_EMAIL } from '~/constants';
import usePreferences from '../../../hooks/use-preferences';
import ToastService from '../../../services/toast-service';
import ProjectService from '../../../services/project-service';
import AsperaOnboardingService from '../../../services/aspera-onboarding-service';

import { EmailPreference, IngestContainer, NotificationsContainer, TabsContent } from './preferences.css';
import { DEFAULT_TAB } from '~/modules/hooks/use-preferences-route';

import ContactListPreferences from '~/modules/components/global/preferences/contact-lists';
import ProjectPreferences from '~/modules/components/global/preferences/projects';
import AssetTypePreferences from '~/modules/components/global/preferences/asset-types';
import { captureError } from '../../../pixwel/exception-handler.factory';
import useAuth from '~/modules/hooks/use-auth';
import { useQuery } from '@tanstack/react-query';
import ProfilePreferences from '~/modules/components/global/preferences/profile-preferences';
import ApiTokens from '~/modules/components/global/preferences/api-tokens';
import Webhooks from '~/modules/components/global/preferences/webhooks';
import ServiceAccounts, { manageableGroups } from '~/modules/components/global/preferences/service-accounts';
import { useTerritories } from '~/modules/hooks/use-territories';
import { useAssetTypes } from '~/modules/hooks/use-asset-types';

function Preferences(props) {
	const {
		show = false,
		onClose,
		tab,
		onTabSelected
	} = props;

	// The selected tab is owned by the URL when the caller supplies it, so a refresh lands back
	// on the same tab. Local state is the fallback for any caller that does not.
	const [localTab, setLocalTab] = useState(DEFAULT_TAB);
	const requestedTab = tab || localTab;
	const setActiveTab = onTabSelected || setLocalTab;
	const isAsperaEnabled = useSyncExternalStore(
		AsperaOnboardingService.subscribe,
		AsperaOnboardingService.isEnabled
	);
	const [isEnablingAspera, setIsEnablingAspera] = useState(false);
	const [preferences, setPreferences] = useState(null);
	const [user, setUser] = useState(null);
	const [projectParams, setProjectParams] = useState({ archived: false, limit: 50, search: '' });
	const { preferences: currentPreferences, isSaving, updatePreferences } = usePreferences();
	const { session, updateUser, isUserSaving } = useAuth();

	// Shown only to people actually named as a manager on at least one group. An admin who is not
	// named sees no tab: they hold the capability implicitly on the API, but there would be no group
	// for them to create in here, and they manage groups from the Admin area anyway.
	const canManageServiceAccounts = useMemo(
		() => manageableGroups(session).length > 0,
		[session]
	);

	// Profile first: it holds the account's own name, address and second factor, which is what
	// people open this for. Notification volume settings are a once-a-year visit.
	const tabKeys = [
		'profile',
		'notifications',
		'ingest',
		'custom-groups',
		'api-tokens',
		'webhooks',
		...(canManageServiceAccounts ? ['service-accounts'] : [])
	];
	const tabs = tabKeys.map((key) => ({
		key,
		label: key
			.split('-')
			.map((w) => w[0].toUpperCase() + w.slice(1))
			.join(' ')
	}));

	// The URL names the tab, and a URL can name anything: a tab that does not exist, or one this
	// person was deliberately not given. Either opens on Profile rather than on an empty panel --
	// or on content that was meant to be absent.
	const activeTab = tabKeys.includes(requestedTab) ? requestedTab : DEFAULT_TAB;
	const { assetTypes, isLoading: areAssetTypesLoading } = useAssetTypes();

	const { isInitialLoading: areProjectsLoading, data: projects } = useQuery(
		['getProjectsPreferences', projectParams],
		() => ProjectService.getAllProjectsShort(projectParams),
		{
			refetchOnWindowFocus: false,
			enabled: show
		}
	);

	const { territories } = useTerritories({
		staleTime: 8 * 60 * 60 * 1000, // 8 hours
		cacheTime: 8 * 60 * 60 * 1000 // 8 hours
	});

	const territoriesOptions = _.isArray(territories) && territories.map((territory) => {
		return ({
			label: territory.name,
			value: territory.id
		});
	}) || [];

	const assetTypeOptions = useMemo(() => {
		if(!_.isArray(assetTypes)) {
			return [];
		}

		return assetTypes.map((assetType) => ({
			label: assetType.name,
			value: assetType.id || assetType.name
		}));
	}, [assetTypes]);

	useEffect(() => {
		if( session ) {
			setUser({
				name: session.name,
				emailNormal: session.emailNormal,
				territory: session.territory,
				hasEnforced2fa: session?.hasEnforced2fa
			});
		}
	}, [session]);

	useEffect(() => {
		if( projects?.totalCount > projects?.nodes?.length && !areProjectsLoading ) {
			setProjectParams({	...projectParams, limit: projects.totalCount });
		}
	}, [projects?.totalCount]);

	useEffect(() => {
		if( currentPreferences ) {
			setPreferences(currentPreferences);
		}
	}, [currentPreferences]);

	const handlePreferencesChange = (type, field, value) => {
		setPreferences({
			...preferences,
			[type]: {
				...preferences[type],
				[field]: value
			}
		});
	};

	/**
	 * Whether the profile fields differ from the session we loaded them from.
	 *
	 * One answer for both the button's state and the save itself: those used to disagree, the
	 * button comparing territories by value and the save by reference.
	 */
	const isProfileChanged = () => {
		return !!user && (user.name !== session.name
			|| !_.isEqual(user.territory, session.territory));
	};

	// The model requires a name, so an empty one is refused by the API rather than saved as a
	// blank. The button stays down instead, and the field says why.
	const isProfileValid = () => !!user?.name?.trim();

	const isSaveDisabled = () => {
		if(isSaving || isUserSaving || !isProfileValid()) {
			return true;
		}

		return _.isEqual(currentPreferences, preferences) && !isProfileChanged();
	};

	const handleSave = async() => {
		try {
			if(isProfileChanged()) {
				await updateUser({ ...user, name: user.name.trim() });
			}
			if(!_.isEqual(currentPreferences, preferences)) {
				await updatePreferences(preferences);
			}
			onClose();
			ToastService.create('info', 'User preferences saved');
		}
		catch(err) {
			captureError(err, `Error saving preferences: ${err.message}`);
		}
	};

	const handleCancel = () => {
		setPreferences(currentPreferences);
		setBlacklistedProjects(currentPreferences?.blacklist?.projects || []);
		setUser({
			name: session.name,
			emailNormal: session.emailNormal,
			territory: session.territory,
			hasEnforced2fa: session?.hasEnforced2fa
		});
		onClose();
	};

	const renderTabsContent = (activeTab) => {
		switch(activeTab) {
			case 'notifications':
				return renderNotifications();
			case 'ingest':
				return renderIngest();
			case 'profile':
				return renderProfile();
			case 'custom-groups':
				return renderContactLists();
			case 'api-tokens':
				return <ApiTokens />;
			case 'webhooks':
				return <Webhooks />;
			case 'service-accounts':
				return <ServiceAccounts />;

			default:
				return null;
		}
	};

	const renderNotifications = () => {
		return (
			<NotificationsContainer data-testid="preferences-notifications">
				<fieldset>
					<legend className="h5">
						Email Notifications
						<input
							type="checkbox"
							name="enableEmailNotifications"
							id="enableEmailNotifications"
							checked={preferences.mail.enabled}
							onChange={(e) => handlePreferencesChange('mail', 'enabled', e.target.checked)}
						/>
					</legend>
					{ renderNotificationsEmail() }
				</fieldset>
				<fieldset className="preferences-notifications-projects">
					<legend className="h5">Projects Notifications disabled :</legend>
					{ renderProjects() }
				</fieldset>
				<fieldset className="preferences-notifications-asset-types">
					<legend className="h5">Asset Type Notifications disabled :</legend>
					{ renderAssetTypes() }
				</fieldset>
			</NotificationsContainer>
		);
	};

	const renderNotificationsEmail = () => {
		const emailOptions = [
			{
				label: 'Immediate',
				value: 0
			},
			{
				label: 'Hourly',
				value: 3600
			},
			{
				label: 'Daily',
				value: 86400
			},
			{
				label: 'Weekly',
				value: 604800
			},
			{
				label: 'Off',
				value: -1
			}
		];

		return PREFERENCES_EMAIL.map((opt) => (
			<EmailPreference key={opt.value}>
				<label>{opt.label}</label>
				<SelectMenu
					className="email-preference"
					options={emailOptions}
					value={emailOptions.find((o) => o.value === preferences.mail[opt.value])}
					onChange={(selected) => handlePreferencesChange('mail', opt.value, selected.value)}
					isDisabled={!preferences.mail.enabled}
				/>
			</EmailPreference>
		));
	};

	// Blacklisted projects selection (for input state)
	const [blacklistedProjects, setBlacklistedProjects] = useState([]);

	// Current blacklisted projects
	const currentBlacklist = currentPreferences?.blacklist?.projects || [];
	useEffect(() => {
		if( currentBlacklist.length && show && projects?.totalCount && projectParams.search === '') {
			const blacklisted = currentBlacklist.map((id) => {
				return projects?.nodes.find((p) => p.value === id) || null;
			});
			setBlacklistedProjects(blacklisted.filter((e) => e) || []);
		}
	}, [currentBlacklist, projects, show]);

	const blacklistedAssetTypes = useMemo(() => {
		const assetTypeBlacklist = preferences?.blacklist?.assetTypes || [];
		return assetTypeBlacklist
			.map((id) => {
				return assetTypeOptions.find((assetType) => assetType.value === id) || null;
			})
			.filter((e) => e);
	}, [assetTypeOptions, preferences?.blacklist?.assetTypes]);

	const handleProjectsPreferencesChange = (selected) => {
		const projectIds = selected.map((p) => p.value);
		handlePreferencesChange('blacklist', 'projects', projectIds);
		setBlacklistedProjects(selected);
	};

	const handleAssetTypesPreferencesChange = (selected) => {
		const assetTypeIds = selected.map((assetType) => assetType.value);
		handlePreferencesChange('blacklist', 'assetTypes', assetTypeIds);
	};

	const renderProjects = () => {
		return (
			<ProjectPreferences
				projects={blacklistedProjects}
				options={projects?.nodes || []}
				setProjects={handleProjectsPreferencesChange}
				isLoading={ areProjectsLoading }
			/>
		);
	};

	const renderAssetTypes = () => {
		return (
			<AssetTypePreferences
				assetTypes={blacklistedAssetTypes}
				options={assetTypeOptions}
				setAssetTypes={handleAssetTypesPreferencesChange}
				isLoading={areAssetTypesLoading}
			/>
		);
	};

	// Turning it on asks the browser for the permission there and then, so the
	// toggle settles into its real state rather than promising something the next
	// transfer has to make good on.
	const handleAsperaToggle = async(isChecked) => {
		if( !isChecked ) {
			AsperaOnboardingService.disable();
			return;
		}

		setIsEnablingAspera(true);

		try {
			await AsperaOnboardingService.enable();
		}
		finally {
			setIsEnablingAspera(false);
		}
	};

	const renderIngest = () => {
		return (
			<IngestContainer data-testid="preferences-ingest">
				<fieldset>
					<div>
						<label>Disable scratch preview for ingests (speeds up processing)</label>
						<input
							type="checkbox"
							name="skipScratchPreview"
							id="skipScratchPreview"
							data-testid="preferences-ingest-skipScratchPreview-input"
							checked={preferences.ingest?.skipScratchPreview}
							onChange={(e) => handlePreferencesChange('ingest', 'skipScratchPreview', e.target.checked)}
						/>
					</div>
					<div>
						<label>Maximum concurrent uploads (limited by your existing bandwidth)</label>
						<input
							type="text"
							name="maxConcurrentUploads"
							id="maxConcurrentUploads"
							data-testid="preferences-ingest-maxConcurrentUploads-input"
							value={preferences.ingest?.maxUploads}
							onChange={(e) => handlePreferencesChange('ingest', 'maxUploads', e.target.value)}
						/>
					</div>
					<div>
						<label>Auto-close ingest modal after Processing</label>
						<input
							type="checkbox"
							name="ingestModalAutoclose"
							id="ingestModalAutoclose"
							data-testid="preferences-ingest-ingestModalAutoclose-input"
							checked={preferences.ingest?.ingestModalAutoclose || false}
							onChange={(e) => handlePreferencesChange('ingest', 'ingestModalAutoclose', e.target.checked)}
						/>
					</div>

					{ /* Held in this browser rather than on the account: it records a
					     decision about this machine's Aspera install, and it is the only
					     way back for someone who turned the offer down. */ }
					<div>
						<label htmlFor="asperaEnabled">Use Aspera for transfers (this browser)</label>
						<input
							type="checkbox"
							name="asperaEnabled"
							id="asperaEnabled"
							data-testid="preferences-ingest-aspera-toggle"
							checked={isAsperaEnabled}
							disabled={isEnablingAspera}
							onChange={(e) => handleAsperaToggle(e.target.checked)}
						/>
					</div>
				</fieldset>
			</IngestContainer>
		);
	};

	const renderProfile = () => {
		return <ProfilePreferences
			session={session}
			territoriesOptions={territoriesOptions}
			user={user}
			setUser={setUser}
			handlePreferencesChange={handlePreferencesChange}
			preferences={preferences}
		/>;
	};

	const renderContactLists = () => {
		return (
			<ContactListPreferences
				currentPreferences={currentPreferences}
				isSaving={isSaving}
				updatePreferences={updatePreferences}
				session={session}
			/>
		);
	};

	if( !preferences ) {
		return null;
	}

	return (
		<Modal
			isOpen={show}
			contentLabel="Preferences"
			size="lg"
			shouldCloseOnOverlayClick={false}
			onRequestClose={onClose}
		>
			<Modal.Header>
				<h3>Preferences</h3>
			</Modal.Header>

			<Modal.Body style={{ paddingTop: '0' }}>
				<Tabs
					name="preferences"
					tabs={tabs}
					selectedTab={activeTab}
					onTabSelected={(tab) => setActiveTab(tab)}
				/>
				<TabsContent data-testid="preferences-tabsContent">
					{ renderTabsContent(activeTab) }
				</TabsContent>
			</Modal.Body>

			<Modal.Footer>
				<Button
					variant="secondary"
					lighten
					onClick={handleCancel}
					disabled={isSaving}
					data-testid="preferences-cancel-btn"
				>
					Cancel
				</Button>

				<Button
					onClick={handleSave}
					loading={isSaving}
					disabled={isSaveDisabled()}
					data-testid="preferences-save-btn"
				>
					Save Preferences
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

Preferences.propTypes = {
	show: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	tab: PropTypes.string,
	onTabSelected: PropTypes.func
};

export default Preferences;
